import { FieldType, FieldOptionFullOutput, OptionItem, FieldView, FieldSignatureOutput } from "@brushfire/types";
import React, { useEffect, useState, useRef, useContext, ReactNode } from "react";
import { Form, Icon, Tooltip } from "../..";
import { Col, Row } from "../../..";
import { Field, FormikContextType, useFormikContext } from "formik";
import { isEmpty, get as _get } from "lodash";
import { GeoJsonProperties } from "geojson";
import { AutofillRetrieveResponse } from "@mapbox/search-js-core";
import { AddressAutofill } from "@mapbox/search-js-react";
import { UtilitiesContext } from "../../../Context";
import CurrencyInput from "../CurrencyInput/CurrencyInput";
import Error from "../Error";
import ReactCountryFlag from "react-country-flag";
import emojiSupport from "detect-emoji-support";
import { IntlShape } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import mailcheck from "mailcheck";
import EmailSuggestion from "../EmailSuggestion";

export interface BrushfireFieldProps {
	className?: string;
	fieldItem: FieldView;
	id: string;
	name: string;
	prefix?: string;
	currency?: string;
	remoteValidate?: (fieldId: string, value: string, setRemoteData: (data: string | boolean) => void) => void;
	remoteFetch?: (fieldId: string, value: string, setRemoteData: (data: string[]) => void) => void;
	autoComplete?: boolean;
	compact?: boolean;
	defaultCountry: string;
	verbiage?: string;
	setDefaultCountry?: React.Dispatch<React.SetStateAction<string>>;
	children?: ReactNode;
	intl: IntlShape;
}

enum suffixes {
	First = "First",
	Last = "Last",
	Street1 = "Street1",
	Street2 = "Street2",
	City = "City",
	Region = "Region",
	Country = "Country",
	PostalCode = "PostalCode",
}

interface SignatureValue {
	integrationId: string;
	thirdPartyId: string;
	templateId: string;
	envelopeId: string;
	isEnvelopeCompleted: boolean;
	roles: {
		roleName: string;
		email: string;
		name: string;
		userId: string;
		isSigned: boolean;
		recipientId: string;
		resend: boolean;
	}[];
}

const convertCurrencyStrToDecimal = (currencyStr: string, intl: IntlShape, currency?: string) => {
	let currencyAmount: number = 0;
	const v = intl.formatNumberToParts(1000.0, {
		style: "currency",
		currency: currency ?? "USD",
	});
	const symbol = v.find((x) => x.type === "currency")?.value || "$";
	const isCommaDecimal = v.find((x) => x.type === "decimal")?.value === ",";

	currencyAmount = currencyStr
		? currencyStr === ""
			? 0
			: isCommaDecimal
			? parseFloat(
					currencyStr
						?.replace(/\s+/g, "")
						.replace(",", ".")
						.replace(symbol || "$", "") || "0.00"
			  )
			: parseFloat(
					currencyStr
						?.replace(/\s+/g, "")
						.replace(/,/g, "")
						.replace(symbol || "$", "") || "0.00"
			  )
		: 0;
	return currencyAmount;
};

const countriesWithRegions = ["AU", "CA", "GB", "US", "ZA"];
const BrushfireField: React.FC<BrushfireFieldProps> = ({
	fieldItem,
	id,
	name,
	prefix = "",
	remoteValidate,
	remoteFetch,
	currency,
	autoComplete = true,
	compact = false,
	defaultCountry,
	verbiage,
	setDefaultCountry,
	intl,
	...props
}) => {
	const [countryMenuOpen, setCountryMenuOpen] = useState(false);
	const [regionFocused, setRegionFocused] = useState(false);
	const [useMapBoxAutocomplete, setUseMapBoxAutocomplete] = useState<boolean>(false);
	const [addressOneAutocomplete, setAddressOneAutocomplete] = useState<string>("address-line1");
	const [countrySearchValue, setCountrySearchValue] = useState("");
	const [emailSuggestion, setEmailSuggestion] = useState<{ [name: string]: string }>({});
	const [renderSecondInput, setRenderSecondInput] = useState<boolean>(false);

	//@ts-ignore
	if (!isNaN(fieldItem.type)) {
		//@ts-ignore
		fieldItem.type = FieldType[fieldItem.type];
	}

	const lastNameRef = useRef<any>(null);

	let isRequired: boolean = false;

	let value: string | null = null;

	isRequired = fieldItem.isRequired;
	value = fieldItem.value;

	const formikContext = useFormikContext<any>();

	const { localization } = useContext(UtilitiesContext);

	//@ts-ignore
	const canUseEmojis = emojiSupport();

	const country = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Country));
	const street1 = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Street1));
	const street2 = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Street2));
	const city = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.City));
	const region = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Region));
	const postalCode = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.PostalCode));

	const choosePlace = (place: AutofillRetrieveResponse, formikContext: FormikContextType<any>) => {
		const attributes: GeoJsonProperties = place.features[0].properties;
		let rstreet1: string | undefined = attributes.address_line1;
		let rstreet2: string | undefined = attributes.address_line2;
		let rcity: string | undefined = attributes.address_level2;
		let rregion: string | undefined = attributes.region_code?.toUpperCase() ?? attributes.region;
		let rcountry: string | undefined = attributes.country_code?.toUpperCase();
		let rpostalCode: string | undefined = attributes.postcode;

		formikContext.setFieldValue(`${prefix}${street1?.id}`, rstreet1);
		formikContext.setFieldValue(`${prefix}${street2?.id}`, rstreet2);
		formikContext.setFieldValue(`${prefix}${city?.id}`, rcity);
		formikContext.setFieldValue(`${prefix}${region?.id}`, rregion);
		formikContext.setFieldValue(`${prefix}${country?.id}`, rcountry);
		formikContext.setFieldValue(`${prefix}${postalCode?.id}`, rpostalCode);

		if (!rstreet1 || !rcity || !rregion || !rcountry || !rpostalCode) {
			formikContext.setFieldTouched(`${prefix}${country?.id}`);
			formikContext.setFieldTouched(`${prefix}${street1?.id}`);
			formikContext.setFieldTouched(`${prefix}${city?.id}`);
			formikContext.setFieldTouched(`${prefix}${region?.id}`);
			formikContext.setFieldTouched(`${prefix}${postalCode?.id}`);
		}
	};

	const addressOneValue: string | undefined = _get(formikContext.values, `${prefix}${street1?.id}`);

	useEffect(() => {
		if (!!addressOneValue && addressOneValue !== "") {
			if (!useMapBoxAutocomplete) {
				setUseMapBoxAutocomplete(true);
				setAddressOneAutocomplete("new-password");
			}
		} else if (useMapBoxAutocomplete) {
			setUseMapBoxAutocomplete(false);
			setAddressOneAutocomplete("new-off");
		}
	}, [addressOneValue]);

	useEffect(() => {
		if (fieldItem.type !== FieldType[FieldType.Name]) {
			const { value, confirmValue } = formikContext.values[name] ?? {};

			if (value === null && confirmValue === null) {
				setRenderSecondInput(true);
			} else if (confirmValue === "") {
				setRenderSecondInput(true);
			}
		} else {
			const first = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.First));
			const last = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Last));

			const firstValues = formikContext.values[first?.id ?? fieldItem.id] ?? {};
			const lastValues = formikContext.values[last?.id ?? fieldItem.id] ?? {};

			if (
				(firstValues.value === null && firstValues.confirmValue === null) ||
				(lastValues.value === null && lastValues.confirmValue === null)
			) {
				setRenderSecondInput(true);
			}
		}
	}, [formikContext.values[name]]);

	const handleChange = (event: any) => {
		formikContext.handleChange(event);

		//if they edit the main input, reset the second input and display it
		if (fieldItem.type === FieldType[FieldType.Name]) {
			const target = event.target.name;
			const targetName = target.substr(0, target.lastIndexOf("."));

			formikContext.setFieldValue(`${targetName}.confirmValue`, "");
			setRenderSecondInput(true);
		} else {
			formikContext.setFieldValue(`${name}.confirmValue`, "");
		}
	};

	const dropHasError =
		formikContext.errors.hasOwnProperty(fieldItem.id) &&
		formikContext.touched.hasOwnProperty(fieldItem.id) &&
		formikContext.submitCount > 0;

	const hasError =
		formikContext.errors.hasOwnProperty(fieldItem.id) &&
		(formikContext.touched.hasOwnProperty(fieldItem.id) || formikContext.submitCount > 0);

	const currentCountry =
		prefix !== ""
			? formikContext.values[prefix.replace(".", "")]?.[country?.id ?? ""]
			: formikContext.values[`${prefix}${country?.id}`];

	const regionOptions = () => {
		if (currentCountry === "AU") {
			return (
				localization?.regionListAU.map((r) => ({
					value: r.code,
					label: r.name,
				})) ?? []
			);
		} else if (currentCountry === "CA") {
			return (
				localization?.regionListCA.map((r) => ({
					value: r.code,
					label: r.name,
				})) ?? []
			);
		} else if (currentCountry === "GB") {
			return (
				localization?.regionListGB.map((r) => ({
					value: r.code,
					label: r.name,
				})) ?? []
			);
		} else if (currentCountry === "US") {
			return (
				localization?.regionListUS.map((r) => ({
					value: r.code,
					label: r.name,
				})) ?? []
			);
		} else if (currentCountry === "ZA") {
			return (
				localization?.regionListZA.map((r) => ({
					value: r.code,
					label: r.name,
				})) ?? []
			);
		}
	};

	const currentRegion =
		prefix !== ""
			? formikContext.values[prefix.replace(".", "")]?.[region?.id ?? ""]
			: formikContext.values[`${prefix}${region?.id}`];

	const showCustomRegion =
		!countriesWithRegions.includes(currentCountry) ||
		(currentRegion && !regionOptions()?.find((x) => x.value === currentRegion));
	const getField = () => {
		switch (fieldItem.type.toLowerCase()) {
			case FieldType[FieldType.Text].toLowerCase():
				return fieldItem.confirm ? (
					<>
						<Form.Field
							label={fieldItem.label}
							id={`${prefix}${id}`}
							name={`${prefix}${name}.value`}
							isFieldRequired={isRequired}
							onChange={handleChange}
							{...props}
						/>
						{renderSecondInput && (
							<>
								<span className="mini text-muted">Please confirm your entry</span>
								<Form.Field
									label={fieldItem.label}
									id={`${prefix}${id}Confirm`}
									name={`${prefix}${name}.confirmValue`}
									isFieldRequired={isRequired}
									hiddenLabel={true}
									{...props}
								/>
							</>
						)}
					</>
				) : (
					<Form.Field
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.Name].toLowerCase():
				const first = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.First));
				const last = fieldItem.childFields?.find((c) => c.specialFieldLabel?.includes(suffixes.Last));
				const nameLabel = fieldItem.label?.replace(" (First)", "");
				return fieldItem.confirm ? (
					<>
						<Form.Input.NameInput
							isRequired={isRequired}
							fieldLabel={nameLabel}
							firstNameId={`${prefix}${first?.id}`}
							firstNameName={`${prefix}${first?.id}.value`}
							firstNameLabel={first?.label}
							lastNameId={`${prefix}${last?.id}`}
							lastNameName={`${prefix}${last?.id}.value`}
							lastNameLabel={last?.label}
							autoComplete={autoComplete}
							handleChange={handleChange}
						/>
						{renderSecondInput && (
							<>
								<span className="mini text-muted">Please confirm your entry</span>
								<Form.Input.NameInput
									isRequired={isRequired}
									fieldLabel={nameLabel}
									firstNameId={`${prefix}${first?.id}Confirm`}
									firstNameName={`${prefix}${first?.id}.confirmValue`}
									firstNameLabel={first?.label}
									lastNameId={`${prefix}${last?.id}Confirm`}
									lastNameName={`${prefix}${last?.id}.confirmValue`}
									lastNameLabel={last?.label}
									autoComplete={autoComplete}
									hiddenLabel={true}
									confirmation={fieldItem.confirm}
								/>
							</>
						)}
					</>
				) : (
					<Form.Input.NameInput
						isRequired={isRequired}
						fieldLabel={nameLabel}
						firstNameId={`${prefix}${first?.id}`}
						firstNameName={`${prefix}${first?.id}`}
						firstNameLabel={first?.label}
						lastNameId={`${prefix}${last?.id}`}
						lastNameName={`${prefix}${last?.id}`}
						lastNameLabel={last?.label}
						autoComplete={autoComplete}
					/>
				);
				break;
			case FieldType[FieldType.Dropdown].toLowerCase():
				const dropdownOptions = fieldItem.options.map((opt: OptionItem | FieldOptionFullOutput) => ({
					value: opt.id,
					label: `${opt.label}${
						(opt.capacity ?? 1) <= 0 && !opt.isSelected
							? " SOLD OUT"
							: ` ${opt.priceDescription || ""}${
									opt.remainingText !== null &&
									opt.remainingText !== undefined &&
									opt.spotsRemaining !== null
										? ` (${opt.remainingText})`
										: ""
							  }`
					}`,
					isDisabled: (opt.capacity ?? 1) <= 0 && !opt.isSelected,
				}));
				return (
					<div className="field" data-field-error-name={`${prefix}${name}`}>
						<Form.Field
							component={Form.Input.Select}
							label={fieldItem.label}
							id={`${prefix}${id}[0].id`}
							name={`${prefix}${name}[0].id`}
							isFieldRequired={isRequired}
							data={dropdownOptions}
							hasError={dropHasError}
							errorValue={formikContext.errors[fieldItem.id]}
							{...props}
						/>
					</div>
				);
				break;
			case FieldType[FieldType.Address].toLowerCase():
				return (
					<div className="field m-bottom">
						<Row>
							<Col xs={12}>
								<div className={`field m-top-0 m-bottom-pull`}>
									<label htmlFor="googleAddressLookup">
										{fieldItem.label} {!isRequired && <span className="optional">- Optional</span>}
									</label>
								</div>
							</Col>
							<Col sm={compact ? 12 : 7}>
								<AddressAutofill
									accessToken="pk.eyJ1IjoiYnJ1c2hmaXJlbWFwcyIsImEiOiJjbDR5bDZmNDgyemhtM29xejZycnM2ZDh1In0.hKM61GLzF2f831lznC0KoQ"
									onRetrieve={(featureCollection) => choosePlace(featureCollection, formikContext)}
									theme={{
										icons: {
											addressMarker: "",
											street: "",
										},
									}}
									options={{
										streets: false,
									}}
								>
									<Form.Field
										label={fieldItem.label}
										hiddenLabel
										id={`${prefix}${street1?.id}`}
										name={`${prefix}${street1?.id}`}
										placeholder="Address"
										autoComplete={autoComplete ? addressOneAutocomplete : "new-off"}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											formikContext.setFieldValue(`${prefix}${street1?.id}`, e.target.value)
										}
										{...props}
									/>
								</AddressAutofill>
							</Col>
							<Col sm={compact ? 12 : 5}>
								<Form.Field
									label=""
									hiddenLabel
									id={`${prefix}${street2?.id}`}
									name={`${prefix}${street2?.id}`}
									placeholder="Address 2 (optional)"
									autoComplete={autoComplete ? "address-line2" : "new-off"}
									{...props}
								/>
							</Col>
						</Row>
						<Row className="">
							<Col sm={compact ? 12 : 6}>
								<Form.Field
									label=""
									hiddenLabel
									id={`${prefix}${city?.id}`}
									name={`${prefix}${city?.id}`}
									placeholder="City"
									autoComplete={autoComplete ? "address-level2" : "new-off"}
									{...props}
								/>
							</Col>
							<Col sm={compact ? 6 : 3} xs={6}>
								<div
									className={`country-region-picker ${countryMenuOpen ? "active" : ""} ${
										regionFocused &&
										currentRegion &&
										!regionOptions()?.find((x) => x.value === currentRegion)
											? "focus"
											: ""
									}`}
								>
									<Form.Field
										component={Form.Input.Select}
										className="field country-region-picker-country notranslate"
										label=""
										hiddenLabel
										id={`${prefix}${country?.id}`}
										name={`${prefix}${country?.id}`}
										isFieldRequired={isRequired}
										getOptionLabel={(o: { value: string; label: string }) => (
											<span tabIndex={-1}>
												{canUseEmojis && (
													<span className="m-right-sm">
														<ReactCountryFlag countryCode={o.value} />
													</span>
												)}
												{countryMenuOpen && <>{o.label}</>}
											</span>
										)}
										data={
											localization
												? localization.countryList.map((c) => ({
														value: c.code,
														label: c.name,
												  }))
												: [{ value: "US", label: "United States" }]
										}
										inputProps={{ autoComplete: autoComplete ? "country" : "new-off" }}
										autoComplete={autoComplete ? "country" : "new-off"}
										placeholder="🏳️"
										isSearchable={true}
										onInputChange={(value: string) => {
											if (!countryMenuOpen) {
												const countryData = localization?.countryList.find(
													(c) =>
														c.code.toLowerCase() === value.toLowerCase() ||
														c.name.toLowerCase() === value.toLowerCase()
												);
												if (countryData) {
													setTimeout(() => {
														setCountrySearchValue("");
														formikContext.setFieldValue(
															`${prefix}${country?.id}`,
															countryData.code
														);
														setCountryMenuOpen(false);
													}, 150);
												}
											}
											setCountrySearchValue(value);
										}}
										menuIsOpen={countryMenuOpen}
										onFocus={() => {
											setCountryMenuOpen(true);
										}}
										onBlur={() => {
											setCountryMenuOpen(false);
										}}
										onChange={(val: any) => {
											formikContext.setFieldValue(`${prefix}${region?.id}`, "");
											setDefaultCountry && setDefaultCountry(val?.value);
										}}
										tabIndex={0}
										tabSelectsValue={false}
										inputValue={countrySearchValue}
										filterOption={(
											{
												label,
												value,
												data,
											}: { label: string; value: string; data: { label: string; value: string } },
											search: string
										) => {
											if (search) {
												return (
													data.label.toLowerCase().includes(search.toLowerCase()) ||
													value
														?.toString()
														.toLowerCase()
														.includes(search.toLowerCase())
												);
											} else {
												return true;
											}
										}}
										{...props}
									/>
									<div className="country-region-picker-divider"></div>
									{showCustomRegion ? (
										<Form.Field
											label=""
											hiddenLabel
											id={`${prefix}${region?.id}`}
											name={`${prefix}${region?.id}`}
											className="field country-region-picker-region text-input"
											placeholder="Province/Region/State"
											autoComplete={autoComplete ? "address-level1" : "new-off"}
											onFocus={() => {
												setRegionFocused(true);
											}}
											onBlur={() => {
												setRegionFocused(false);
											}}
											tabIndex={0}
											{...props}
										/>
									) : (
										<>
											<Form.Field
												component={Form.Input.Select}
												label=""
												hiddenLabel
												id={`${prefix}${region?.id}`}
												name={`${prefix}${region?.id}`}
												data={regionOptions()}
												className="field country-region-picker-region select-input"
												placeholder={
													["AU", "US"].includes(currentCountry)
														? "State"
														: currentCountry === "GB"
														? "County"
														: "Province"
												}
												autoComplete={autoComplete ? "address-level1" : "new-off"}
												inputProps={{
													autoComplete: autoComplete ? "address-level1" : "new-off",
												}}
												onFocus={() => {
													setRegionFocused(true);
												}}
												onBlur={() => {
													setRegionFocused(false);
												}}
												tabSelectsValue={false}
												isSearchable={true}
												menuIsOpen={regionFocused}
												onInputChange={(value: string) => {
													if (!regionFocused) {
														const regionData = regionOptions()?.find(
															(c) =>
																c.value.toLowerCase() === value.toLowerCase() ||
																c.label.toLowerCase() === value.toLowerCase()
														);
														if (regionData) {
															formikContext.setFieldValue(
																`${prefix}${region?.id}`,
																regionData.value
															);
														}
													}
												}}
												{...props}
											/>
										</>
									)}
								</div>
							</Col>
							<Col sm={compact ? 6 : 3} xs={6}>
								<Form.Field
									label=""
									hiddenLabel
									id={`${prefix}${postalCode?.id}`}
									name={`${prefix}${postalCode?.id}`}
									placeholder="Postal Code"
									autoComplete={autoComplete ? "postal-code" : "new-off"}
									{...props}
								/>
							</Col>
						</Row>
					</div>
				);
				break;
			case FieldType[FieldType.Radiobuttons].toLowerCase():
				const uniqueRadioFieldId = uuidv4();
				return (
					<div className="field" data-field-error-name={`${prefix}${name}`}>
						<label>
							{fieldItem.label}
							{!isRequired && <span className="optional">- Optional</span>}
						</label>
						<div className={`${hasError ? "error-input" : ""}`}>
							{fieldItem.options.map((opt: OptionItem | FieldOptionFullOutput, index) => {
								const isDisabled = (opt.capacity ?? 1) <= 0 && !opt.isSelected;
								return (
									<div className="brushfire-radio" key={opt.id}>
										<label
											className={`radio ${isDisabled ? "disabled" : ""} ${
												_get(formikContext.values, `${prefix}${name}.[0].id`) === opt.id
													? "active"
													: ""
											}`}
											htmlFor={`${uniqueRadioFieldId}${prefix}${name}${index}`}
										>
											<input
												type="radio"
												id={`${uniqueRadioFieldId}${prefix}${name}${index}`}
												name={`${prefix}${name}`}
												onChange={(e: any) => {
													const { checked } = e.target;
													if (checked) {
														formikContext.setFieldValue(`${prefix}${name}`, [
															{ id: opt.id },
														]);
													}
												}}
												disabled={isDisabled}
												checked={
													_get(formikContext.values, `${prefix}${name}.[0].id`) === opt.id
												}
											/>
											<span>{`${opt.label} ${opt.priceDescription || ""}${
												(opt.capacity ?? 1) <= 0 && !opt.isSelected
													? " SOLD OUT"
													: opt.remainingText !== null &&
													  opt.remainingText !== undefined &&
													  opt.spotsRemaining !== null
													? ` (${opt.remainingText})`
													: ""
											}`}</span>
										</label>
									</div>
								);
							})}
						</div>
						{hasError && <Error>{formikContext.errors[fieldItem.id]}</Error>}
					</div>
				);
				break;
			case FieldType[FieldType.Checkboxes].toLowerCase():
				const uniqueFieldId = uuidv4();
				return (
					<div className="field" data-field-error-name={`${prefix}${name}`}>
						<label>
							{fieldItem.label}
							{!isRequired && <span className="optional">- Optional</span>}
						</label>
						<div className={`${hasError ? "error-input" : ""}`}>
							{fieldItem.options.map((opt: OptionItem | FieldOptionFullOutput, index) => {
								const isDisabled = (opt.capacity ?? 1) <= 0 && !opt.isSelected;
								return (
									<div className="brushfire-checkbox" key={opt.id}>
										<label
											className={`checkbox ${
												_get(formikContext.values, `${prefix}${name}.[${index}].id`) === opt.id
													? "active"
													: ""
											} ${isDisabled ? "disabled" : ""}`}
											htmlFor={`${uniqueFieldId}${prefix}${name}[${index}].id`}
										>
											<input
												type="checkbox"
												id={`${uniqueFieldId}${prefix}${name}[${index}].id`}
												name={`${prefix}${name}[${index}].id`}
												onChange={(e: any) => {
													const { checked } = e.target;
													if (checked) {
														formikContext.setFieldValue(
															`${prefix}${name}[${index}].id`,
															opt.id
														);
													} else if (!checked) {
														formikContext.setFieldValue(`${prefix}${name}[${index}]`, null);
													}
												}}
												checked={
													_get(formikContext.values, `${prefix}${name}.[${index}].id`) ===
													opt.id
												}
												disabled={isDisabled}
											/>
											<span>{`${opt.label} ${opt.priceDescription || ""}${
												(opt.capacity ?? 1) <= 0 && !opt.isSelected
													? " SOLD OUT"
													: opt.remainingText !== null &&
													  opt.remainingText !== undefined &&
													  opt.spotsRemaining !== null
													? ` (${opt.remainingText})`
													: ""
											}`}</span>
										</label>
									</div>
								);
							})}
						</div>
						{hasError && <Error>{formikContext.errors[fieldItem.id]}</Error>}
					</div>
				);
				break;
			case FieldType[FieldType.ItemList].toLowerCase():
				return (
					<div data-field-error-name={`${prefix}${name}`}>
						<label className="field-label-style">{fieldItem.label}</label>

						{fieldItem.options.map((opt: OptionItem | FieldOptionFullOutput, index) => {
							const dropdownOptions = Array.from({ length: (opt.capacity ?? 250) + 1 }, (v, k) => ({
								value: k,
								label: `${k}`,
							}));

							return (
								<div key={opt.id}>
									<Form.Field
										label={opt.label}
										id={`${prefix}${id}[${index}].id`}
										name={`${prefix}${name}[${index}].id`}
										value={""}
										type="hidden"
										hiddenLabel
									/>
									<div className="item-list-field">
										<Form.Field
											component={Form.Input.Select}
											label={`${opt.label} ${opt.priceDescription || ""}${
												(opt.capacity ?? 1) <= 0 && !opt.isSelected
													? " SOLD OUT"
													: opt.remainingText !== null &&
													  opt.remainingText !== undefined &&
													  opt.spotsRemaining !== null
													? ` (${opt.remainingText})`
													: ""
											}`}
											id={`${prefix}${id}[${index}].quantity`}
											name={`${prefix}${name}[${index}].quantity`}
											isFieldRequired={isRequired}
											data={dropdownOptions}
											hasError={dropHasError}
											disabled={(opt.capacity ?? 1) <= 0 && !opt.isSelected}
											onChange={() => {
												if (
													_get(formikContext.values, `${prefix}${name}.[${index}].id`) !==
													opt.id
												) {
													formikContext.setFieldValue(`${prefix}${id}[${index}].id`, opt?.id);
												}
											}}
											{...props}
										/>
										{dropHasError && <Error>{formikContext.errors[fieldItem.id]}</Error>}
									</div>
								</div>
							);
						})}
					</div>
				);
				break;
			case FieldType[FieldType.ItemAmount].toLowerCase():
				return (
					<div data-field-error-name={`${prefix}${name}`}>
						<label className="field-label-style">{fieldItem.label}</label>
						{fieldItem.options.map((opt: OptionItem | FieldOptionFullOutput, index) => {
							return (
								<div key={opt.id}>
									<Form.Field
										label={opt.label}
										id={`${prefix}${id}[${index}].id`}
										name={`${prefix}${name}[${index}].id`}
										value={""}
										type="hidden"
										hiddenLabel
									/>
									<div className="item-list-field">
										<CurrencyInput
											label={`${opt.label} ${opt.priceDescription || ""}`}
											id={`${prefix}${id}[${index}].price`}
											name={`${prefix}${name}[${index}].price`}
											isFieldRequired={isRequired}
											currency={currency ?? "USD"}
											className={props.className}
											onChange={(e: React.ChangeEvent) => {
												formikContext.setFieldValue(
													`${prefix}${id}[${index}].price`,
													convertCurrencyStrToDecimal(
														(e.target as HTMLInputElement).value,
														intl,
														currency ?? "USD"
													)
												);
											}}
											onBlur={(e: React.ChangeEvent) => {
												if (
													(e.target as HTMLInputElement).value &&
													_get(formikContext.values, `${prefix}${name}.[${index}].id`) !==
														opt.id
												) {
													formikContext.setFieldValue(`${prefix}${id}[${index}].id`, opt.id);
												}
											}}
											hasError={dropHasError}
											placeholder={intl.formatNumber(opt.unitPrice ?? 0, {
												style: "currency",
												currency: currency ?? "USD",
											})}
										/>
									</div>
								</div>
							);
						})}
						{dropHasError && <Error>{formikContext.errors[fieldItem.id]}</Error>}
					</div>
				);
				break;
			case FieldType[FieldType.Accept].toLowerCase():
				return (
					<div data-field-error-name={`${prefix}${name}`}>
						<Form.Field
							component={Form.Input.Radio}
							label={fieldItem.label}
							id={`${prefix}${id}`}
							fieldClassName={"brushfire-accept-field"}
							name={`${prefix}${name}`}
							isFieldRequired={isRequired}
							options={[
								{ label: "Yes", value: "True" },
								{
									label: `No${
										_get(formikContext.values, `${prefix}${name}`) === "False"
											? " (You will not be able to continue)"
											: ""
									}`,
									value: "False",
								},
							]}
							{...props}
						/>
					</div>
				);
				break;
			case FieldType[FieldType.Date].toLowerCase():
				return fieldItem.confirm ? (
					<>
						<Form.Field
							label={fieldItem.label}
							id={`${prefix}${id}`}
							name={`${prefix}${name}.value`}
							isFieldRequired={isRequired}
							type="date"
							onChange={handleChange}
							{...props}
						/>
						{renderSecondInput && (
							<>
								<span className="mini text-muted">Please confirm your entry</span>
								<Form.Field
									label={fieldItem.label}
									id={`${prefix}${id}Confirm`}
									name={`${prefix}${name}.confirmValue`}
									isFieldRequired={isRequired}
									type="date"
									hiddenLabel={true}
									{...props}
								/>
							</>
						)}
					</>
				) : (
					<Form.Field
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						type="date"
						confirmation={fieldItem.confirm}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.Phone].toLowerCase():
				return fieldItem.confirm ? (
					<>
						<Form.Field
							component={Form.Input.Phone}
							label={fieldItem.label}
							id={`${prefix}${id}`}
							name={`${prefix}${name}.value`}
							isFieldRequired={isRequired}
							autoComplete={autoComplete}
							defaultCountry={defaultCountry}
							{...props}
						/>
						{renderSecondInput && (
							<>
								<span className="mini text-muted">Please confirm your entry</span>
								<Form.Field
									component={Form.Input.Phone}
									label={fieldItem.label}
									id={`${prefix}${id}Confirm`}
									name={`${prefix}${name}.confirmValue`}
									isFieldRequired={isRequired}
									autoComplete={autoComplete}
									defaultCountry={defaultCountry}
									hiddenLabel={true}
									{...props}
								/>
							</>
						)}
					</>
				) : (
					<Form.Field
						component={Form.Input.Phone}
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						autoComplete={autoComplete}
						defaultCountry={defaultCountry}
						confirmation={fieldItem.confirm}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.Email].toLowerCase():
				return fieldItem.confirm ? (
					<>
						<Form.Field
							label={fieldItem.label}
							id={`${prefix}${id}`}
							name={`${prefix}${name}.value`}
							isFieldRequired={isRequired}
							placeholder="name@domain.com"
							type="email"
							autoComplete={autoComplete ? "email" : "new-off"}
							onChange={handleChange}
							{...props}
						/>
						{renderSecondInput && (
							<>
								<span className="mini text-muted">Please confirm your entry</span>
								<Form.Field
									label={fieldItem.label}
									id={`${prefix}${id}Confirm`}
									name={`${prefix}${name}.confirmValue`}
									isFieldRequired={isRequired}
									placeholder="name@domain.com"
									type="email"
									autoComplete={autoComplete ? "email" : "new-off"}
									displayIsPrimary={fieldItem.displayIsPrimary}
									verbiage={verbiage?.slice(0, -1)?.toLowerCase()}
									hiddenLabel={true}
									{...props}
								/>
							</>
						)}
					</>
				) : (
					<Form.Field
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						placeholder="name@domain.com"
						type="email"
						autoComplete={autoComplete ? "email" : "new-off"}
						displayIsPrimary={fieldItem.displayIsPrimary}
						verbiage={verbiage?.slice(0, -1)?.toLowerCase()}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.MultiText].toLowerCase():
				return (
					<Form.Field
						component={Form.Input.TextArea}
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.Paragraph].toLowerCase():
				return (
					<div className="field">
						<span id={`${prefix}${id}`} className="field-paragraph-label">
							{fieldItem.label}
						</span>
						<div
							aria-labelledby={`${prefix}${id}`}
							style={{ maxHeight: "300px", overflow: "auto" }}
							className="wysiwyg-text"
							dangerouslySetInnerHTML={{ __html: value || "" }}
						></div>
					</div>
				);
				break;
			case FieldType[FieldType.SectionHeader].toLowerCase():
				return <h1 className="field-section-header">{fieldItem.label}</h1>;
				break;
			case FieldType[FieldType.Link].toLowerCase():
				return value ? (
					<div className="field">
						<span id={`${prefix}${id}`} className="mr-1">
							{fieldItem.label}
						</span>
						<a aria-labelledby={`${prefix}${id}`} href={value} target="_blank">
							Click Here
						</a>
					</div>
				) : null;
				break;
			case FieldType[FieldType.RemoteValidate].toLowerCase():
				return (
					<Form.Field
						component={Form.Input.RemoteValidateInput}
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						fieldId={fieldItem.id}
						validate={() => {
							return formikContext.status[fieldItem.id];
						}}
						remoteValidate={remoteValidate}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.RemoteFetch].toLowerCase():
				return (
					<Form.Field
						component={Form.Input.RemoteFetchInput}
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						remoteUrl={fieldItem.remoteUrl}
						remoteFetch={remoteFetch}
						fieldId={fieldItem.id}
						{...props}
					/>
				);
				break;
			case FieldType[FieldType.Signature].toLocaleLowerCase():
				var signatureFieldData = (fieldItem.value as unknown) as FieldSignatureOutput;
				return (
					<div className={`field`} data-field-error-name={`${prefix}${name}`}>
						<Row>
							<Col sm={12}>
								<label>{fieldItem.label}</label>
							</Col>
						</Row>
						<Row>
							<Col sm={signatureFieldData.roles.every((role) => role.isSigned) ? 8 : 12}>
								An electronic signature is required to complete this registration. Please provide the
								information below and instructions will be sent via email to complete the required
								signatures.
							</Col>
							{signatureFieldData.roles.every((role) => role.isSigned) && (
								<Col sm={4}>
									<a
										className="btn btn-block btn-dark btn-dark-link never-disabled"
										href={`https://app.${process.env.REACT_APP_DOMAIN}/sign/${signatureFieldData.integrationId}/download/${signatureFieldData.envelopeId}`}
										target="_blank"
									>
										<Icon icon="download" /> Download
									</a>
								</Col>
							)}
						</Row>
						{signatureFieldData.roles.map((role, index, roles) => {
							const roleFieldName = `${prefix}${id}.roles[${index}].email`;
							const hasNameError =
								formikContext.errors.hasOwnProperty(`${prefix}${id}.roles[${index}].name`) &&
								(formikContext.touched.hasOwnProperty(`${prefix}${id}.roles[${index}].name`) ||
									formikContext.submitCount > 0);
							const hasEmailError =
								formikContext.errors.hasOwnProperty(`${prefix}${id}.roles[${index}].name`) &&
								(formikContext.touched.hasOwnProperty(`${prefix}${id}.roles[${index}].name`) ||
									formikContext.submitCount > 0);
							const hasServerError = typeof formikContext.errors[`${prefix}${id}`] === "string";
							return (
								<Row key={index} className={index === roles.length - 1 ? "m-bottom-pull" : ""}>
									<Col md={!!signatureFieldData.envelopeId ? 5 : 6}>
										<Form.Field
											label={`Name for ${role.roleName}${
												roles.length > 1 ? ` ${index + 1}` : ""
											}`}
											placeholder={`Name for ${role.roleName} ${
												roles.length > 1 ? ` ${index + 1}` : ""
											}`}
											hiddenLabel
											id={`${prefix}${id}.roles[${index}].name`}
											name={`${prefix}${id}.roles[${index}].name`}
											isFieldRequired={isRequired}
											fieldClassName={`${hasError ? "error-input" : ""}`}
											{...props}
										/>
										{hasNameError && (
											<Error>{formikContext.errors[`${prefix}${id}.roles[${index}].name`]}</Error>
										)}
									</Col>
									<Col md={!!signatureFieldData.envelopeId ? 5 : 6}>
										<Form.Field
											label={`Email for ${role.roleName}${
												roles.length > 1 ? ` ${index + 1}` : ""
											}`}
											placeholder={`Email for ${role.roleName}${
												roles.length > 1 ? ` ${index + 1}` : ""
											}`}
											hiddenLabel
											id={roleFieldName}
											name={roleFieldName}
											isFieldRequired={isRequired}
											fieldClassName={`${hasError ? "error-input" : ""}`}
											type="email"
											{...props}
										/>

										{hasEmailError && (
											<Error>
												{formikContext.errors[`${prefix}${id}.roles[${index}].email`]}
											</Error>
										)}
										{!!signatureFieldData.envelopeId && !role.isSigned && (
											<>
												<label className="small">
													<Field
														type="checkbox"
														name={`${prefix}${id}.roles[${index}].resend`}
													/>{" "}
													Resend signature email
												</label>
											</>
										)}
									</Col>
									{!!signatureFieldData.envelopeId && (
										<Col
											md={2}
											className={`signature-status ${role.isSigned ? "signed" : "pending"}`}
										>
											{role.isSigned ? (
												<span title="Signature request signed!">
													<Icon icon="check" /> Signed
												</span>
											) : (
												<span title="Signature request has been sent, but it has not been signed.">
													<Icon icon="clock" /> Pending
												</span>
											)}
										</Col>
									)}

									{hasServerError && <Error>{formikContext.errors[`${prefix}${id}`]}</Error>}
								</Row>
							);
						})}
					</div>
				);
				break;
			default:
				return (
					<Form.Field
						label={fieldItem.label}
						id={`${prefix}${id}`}
						name={`${prefix}${name}`}
						isFieldRequired={isRequired}
						{...props}
					/>
				);
				break;
		}
	};
	return (
		<>
			{!!fieldItem.notes ? (
				<div
					className={`field-with-notes ${
						[
							FieldType[FieldType.Accept].toLowerCase(),
							FieldType[FieldType.Link].toLowerCase(),
							FieldType[FieldType.Paragraph].toLowerCase(),
							FieldType[FieldType.Signature].toLowerCase(),
						].includes(fieldItem.type.toLowerCase())
							? "no-label"
							: ""
					}`}
				>
					<div className="field-wrap">{getField()}</div>
					<div className="field-notes">
						<Tooltip ariaLabel="View Notes">{fieldItem.notes}</Tooltip>
					</div>
				</div>
			) : (
				<>{getField()}</>
			)}
		</>
	);
};

export default BrushfireField;
